/* eslint-disable */

import React from 'react'
import { api } from '../requests/axios'
import { Box, MenuItem, Select, Typography, Grid, Card, CardContent, CardMedia, CircularProgress, Button, TextField, Link, Alert } from '@mui/material'
import { useEffect, useState } from 'react'

export interface YarnCrawlerProps {
  onSelect?: (brand: string, yarn: string, profile: string[], imageUrl?: string | null) => void
  initialBrand?: string
  initialYarn?: string
  initialProfile?: string[]
  initialImageUrl?: string | null
}

function YarnCrawler({ onSelect, initialBrand, initialYarn, initialProfile, initialImageUrl }: YarnCrawlerProps) {
  const [brands, setBrands] = useState<string[]>([])
  const [selectedBrand, setSelectedBrand] = useState<string>(initialBrand || '')
  const [yarns, setYarns] = useState<string[]>([])
  const [selectedYarn, setSelectedYarn] = useState<string>(initialYarn || '')
  const [yarnProfile, setYarnProfile] = useState<string[]>(initialProfile || [])
  const [imageUrl, setImageUrl] = useState<string | null>(initialImageUrl || null)
  const [imageLoadFailed, setImageLoadFailed] = useState(false)
  const [loadingBrands, setLoadingBrands] = useState(false)
  const [loadingYarns, setLoadingYarns] = useState(false)
  const [loadingProfile, setLoadingProfile] = useState(false)
  const [manualMode, setManualMode] = useState(false)
  const [manualFormData, setManualFormData] = useState({
    brand: '',
    name: '',
    length: '',
    length_unit: 'm' as 'm' | 'yd',
    weight: '',
    weight_unit: 'g' as 'g' | 'oz',
    material: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const fetchBrands = async () => {
    setLoadingBrands(true)
    try {
      const response = await api.get((process.env.REACT_APP_API_PROXY_URL || '').concat(`/yarns/brands.json`))
      const data = await response.data
      setBrands(data.brands || [])
    } catch (error) {
      console.error('Error fetching brands:', error)
    } finally {
      setLoadingBrands(false)
    }
  }

  const fetchYarns = async (brand: string, preserveSelection?: boolean) => {
    if (!brand) return
    
    setLoadingYarns(true)
    setYarns([])
    // Only clear selected yarn if we're not preserving it (i.e., not restoring from initial values)
    if (!preserveSelection) {
      setSelectedYarn('')
      setYarnProfile([])
      setImageUrl(null)
    }
    
    try {
      const transformedBrand = brand.replace(/ & /g, ' And ')
      const response = await api.get((process.env.REACT_APP_API_PROXY_URL || '').concat(`/yarns/yarns.json?brand=${encodeURIComponent(transformedBrand)}`))
      const data = await response.data
      setYarns(data.yarns || [])
      // If we're preserving selection and have initialYarn, set it after yarns are loaded
      if (preserveSelection && initialYarn) {
        setSelectedYarn(initialYarn)
      }
    } catch (error) {
      console.error('Error fetching yarns:', error)
    } finally {
      setLoadingYarns(false)
    }
  }

  const fetchProfile = async (brand: string, yarn: string, skipIfRestoring?: boolean) => {
    if (!brand || !yarn) return
    
    // If we're restoring and already have profile data, skip fetching
    if (skipIfRestoring && initialProfile && initialProfile.length > 0 && initialBrand === brand && initialYarn === yarn) {
      return
    }
    
    setLoadingProfile(true)
    // Don't clear profile if we're restoring from initial values
    if (!initialProfile || initialProfile.length === 0) {
      setYarnProfile([])
      setImageUrl(null)
    }
    setImageLoadFailed(false)

    try {
      const transformedBrand = brand.replace(/ & /g, ' And ')
      const response = await api.get((process.env.REACT_APP_API_PROXY_URL || '').concat(`/yarns/profile.json?brand=${encodeURIComponent(transformedBrand)}&yarn=${encodeURIComponent(yarn)}`))
      const data = await response.data
      setYarnProfile(data.profile || [])
      setImageUrl(data.image_url || null)
    } catch (error) {
      console.error('Error fetching profile:', error)
    } finally {
      setLoadingProfile(false)
    }
  }

  useEffect(() => {
    fetchBrands()
  }, [])

  // Initialize with provided values when they change
  useEffect(() => {
    if (initialBrand) {
      setSelectedBrand(initialBrand)
    }
  }, [initialBrand])

  // Set initial yarn after yarns list is loaded (handled in fetchYarns)
  // This useEffect is kept for cases where yarns are already loaded
  useEffect(() => {
    if (initialYarn && yarns.length > 0 && !selectedYarn) {
      // Only set if yarns are already loaded and nothing is selected
      setSelectedYarn(initialYarn)
    }
  }, [initialYarn, yarns.length, selectedYarn])

  useEffect(() => {
    if (initialProfile && initialProfile.length > 0) {
      setYarnProfile(initialProfile)
    }
  }, [initialProfile])

  useEffect(() => {
    if (initialImageUrl !== undefined) {
      setImageUrl(initialImageUrl)
      setImageLoadFailed(false)
    }
  }, [initialImageUrl])

  useEffect(() => {
    if (selectedBrand) {
      // If we have initialYarn, preserve the selection when fetching
      const preserveSelection = !!initialYarn && selectedBrand === initialBrand
      fetchYarns(selectedBrand, preserveSelection)
    }
  }, [selectedBrand, initialBrand, initialYarn])

  useEffect(() => {
    if (selectedBrand && selectedYarn) {
      // Skip fetching profile if we're restoring initial values
      const skipIfRestoring = !!(initialProfile && initialProfile.length > 0 && 
                                 selectedBrand === initialBrand && selectedYarn === initialYarn)
      fetchProfile(selectedBrand, selectedYarn, skipIfRestoring)
    }
  }, [selectedBrand, selectedYarn, initialBrand, initialYarn, initialProfile])

  const handleBrandChange = (event: any) => {
    setSelectedBrand(event.target.value)
  }

  const handleYarnChange = (event: any) => {
    setSelectedYarn(event.target.value)
  }

  const handleManualSubmit = async () => {
    if (!manualFormData.brand || !manualFormData.name || !manualFormData.length || 
        !manualFormData.weight || !manualFormData.material) {
      setSubmitError('Please fill in all required fields')
      return
    }

    setSubmitting(true)
    setSubmitError(null)

    try {
      // Convert length_unit and weight_unit to integers (0 for m/g, 1 for yd/oz)
      const lengthUnitInt = manualFormData.length_unit === 'm' ? 0 : 1
      const weightUnitInt = manualFormData.weight_unit === 'g' ? 0 : 1

      const response = await api.post(
        (process.env.REACT_APP_API_PROXY_URL || '').concat('/yarns.json'),
        {
          yarn: {
            brand: manualFormData.brand,
            name: manualFormData.name,
            length: parseFloat(manualFormData.length),
            length_unit: lengthUnitInt,
            weight: parseFloat(manualFormData.weight),
            weight_unit: weightUnitInt,
            material: manualFormData.material
          }
        }
      )

      const createdYarn = response.data.yarn
      
      // Create a profile array similar to what the API returns
      const profile = [
        `Weight: ${manualFormData.weight} ${manualFormData.weight_unit}`,
        `Length: ${manualFormData.length} ${manualFormData.length_unit}`,
        `Material: ${manualFormData.material}`
      ]

      // Call onSelect with the manually entered data
      if (onSelect) {
        onSelect(createdYarn.brand, createdYarn.name, profile, null)
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.errors || error.message || 'Failed to create yarn'
      setSubmitError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
        Yarn Information
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => {
            setManualMode(!manualMode)
            setSubmitError(null)
          }}
          sx={{ cursor: 'pointer' }}
        >
          {manualMode ? '← Lookup Yarn Instead' : "Can't find your yarn? Add it manually"}
        </Link>
      </Box>

      {manualMode ? (
        <Card
          sx={{
            borderRadius: 3,
            bgcolor: '#FDFBF7',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            maxWidth: 480
          }}
        >
          <Box sx={{ height: 6, bgcolor: 'primary.main', opacity: 0.3 }} />
          <CardContent sx={{ p: 2.5 }}>
            <Typography
              variant="overline"
              sx={{ letterSpacing: 1.5, opacity: 0.8, fontSize: '0.7rem', display: 'block', mb: 0.5 }}
            >
              Brand
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. Malabrigo"
              value={manualFormData.brand}
              onChange={(e) => setManualFormData({ ...manualFormData, brand: e.target.value })}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.6)',
                  fontFamily: 'inherit'
                }
              }}
              required
              size="small"
            />
            <Typography
              variant="overline"
              sx={{ letterSpacing: 1.5, opacity: 0.8, fontSize: '0.7rem', display: 'block', mb: 0.5 }}
            >
              Yarn Name
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. Rios"
              value={manualFormData.name}
              onChange={(e) => setManualFormData({ ...manualFormData, name: e.target.value })}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.6)',
                  fontFamily: 'Georgia, serif',
                  fontWeight: 600
                }
              }}
              required
              size="small"
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>Weight:</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    type="number"
                    placeholder="50"
                    value={manualFormData.weight}
                    onChange={(e) => setManualFormData({ ...manualFormData, weight: e.target.value })}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.6)' }
                    }}
                    size="small"
                    required
                  />
                  <TextField
                    select
                    value={manualFormData.weight_unit}
                    onChange={(e) => setManualFormData({ ...manualFormData, weight_unit: e.target.value as 'g' | 'oz' })}
                    sx={{
                      minWidth: 72,
                      '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.6)' }
                    }}
                    size="small"
                    SelectProps={{ native: true }}
                  >
                    <option value="g">g</option>
                    <option value="oz">oz</option>
                  </TextField>
                </Box>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>Length:</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    type="number"
                    placeholder="100"
                    value={manualFormData.length}
                    onChange={(e) => setManualFormData({ ...manualFormData, length: e.target.value })}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.6)' }
                    }}
                    size="small"
                    required
                  />
                  <TextField
                    select
                    value={manualFormData.length_unit}
                    onChange={(e) => setManualFormData({ ...manualFormData, length_unit: e.target.value as 'm' | 'yd' })}
                    sx={{
                      minWidth: 72,
                      '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.6)' }
                    }}
                    size="small"
                    SelectProps={{ native: true }}
                  >
                    <option value="m">m</option>
                    <option value="yd">yd</option>
                  </TextField>
                </Box>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>Material:</Typography>
            <TextField
              fullWidth
              placeholder="e.g. 100% Merino Wool"
              value={manualFormData.material}
              onChange={(e) => setManualFormData({ ...manualFormData, material: e.target.value })}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.6)' }
              }}
              required
              size="small"
              multiline
              rows={2}
            />
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>
            )}
            {onSelect && (
              <Button
                variant="contained"
                onClick={handleManualSubmit}
                disabled={submitting}
                fullWidth
              >
                {submitting ? 'Creating...' : 'Create Yarn'}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Select Brand</Typography>
            <Select
              fullWidth
              value={selectedBrand}
              onChange={handleBrandChange}
              displayEmpty
              disabled={loadingBrands}
            >
              <MenuItem value="">
                <em>Choose a brand...</em>
              </MenuItem>
              {brands.map((brand) => (
                <MenuItem key={brand} value={brand}>
                  {brand}
                </MenuItem>
              ))}
            </Select>
            {loadingBrands && <CircularProgress size={24} sx={{ mt: 1 }} />}
          </Box>

          {selectedBrand && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Select Yarn</Typography>
              <Select
                fullWidth
                value={selectedYarn}
                onChange={handleYarnChange}
                displayEmpty
                disabled={loadingYarns || !selectedBrand}
              >
                <MenuItem value="">
                  <em>Choose a yarn...</em>
                </MenuItem>
                {yarns.map((yarn) => (
                  <MenuItem key={yarn} value={yarn}>
                    {yarn}
                  </MenuItem>
                ))}
              </Select>
              {loadingYarns && <CircularProgress size={24} sx={{ mt: 1 }} />}
            </Box>
          )}
            <Typography variant="body2" color="text.secondary">* = discontinued yarn</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
          {loadingProfile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loadingProfile && selectedYarn && (
            <Box>
            {imageUrl && !imageLoadFailed && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                <CardMedia
                  component="img"
                  image={imageUrl}
                  alt={selectedYarn}
                  onError={() => setImageLoadFailed(true)}
                  sx={{ maxHeight: 220, objectFit: 'contain' }}
                />
              </Box>
            )}
            <Card
              sx={{
                borderRadius: 3,
                bgcolor: '#FDFBF7',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
              }}
            >
              {/* Decorative band */}
              <Box sx={{ height: 6, bgcolor: 'primary.main', opacity: 0.3 }} />
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  variant="overline"
                  sx={{ letterSpacing: 1.5, opacity: 0.8, fontSize: '0.7rem' }}
                >
                  {selectedBrand}
                </Typography>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{
                    fontFamily: 'Georgia, serif',
                    fontWeight: 600,
                    mb: 1.5,
                    lineHeight: 1.3
                  }}
                >
                  {selectedYarn}
                </Typography>
                    {yarnProfile.length > 0 ? (
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 0.5,
                          mb: 2
                        }}
                      >
                        {yarnProfile.map((info, index) => {
                          const colonIdx = info.indexOf(':')
                          const hasLabel = colonIdx >= 0
                          const label = hasLabel ? info.slice(0, colonIdx) : ''
                          const value = hasLabel ? info.slice(colonIdx + 1).trim() : info
                          return (
                            <Box key={index} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {hasLabel && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  {label}:
                                </Typography>
                              )}
                              <Typography variant="body2">{value}</Typography>
                            </Box>
                          )
                        })}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No profile information available.
                      </Typography>
                    )}
              </CardContent>
            </Card>
            {onSelect && selectedBrand && selectedYarn && yarnProfile.length > 0 && (
              <Button
                variant="contained"
                fullWidth
                onClick={() => onSelect(selectedBrand, selectedYarn, yarnProfile, imageLoadFailed ? null : imageUrl)}
                sx={{ mt: 2 }}
              >
                Use This Yarn
              </Button>
            )}
            </Box>
          )}
          </Grid>
        </Grid>
      )}
    </Box>
  )
}

export default YarnCrawler
